import React, { useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { MdUpload, MdCloudUpload } from "react-icons/md";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import pythonApiClient from "../../../services/api/pythonBaseApi";

const ActualizacionMasivaPlanilla = ({ show, onHide, anio, mes, proyectoId, onExito }) => {
  const fileInputRef = useRef(null);

  const [file,                 setFile]                = useState(null);
  const [fileForRevalidation,  setFileForRevalidation] = useState(null);
  const [isLoading,            setIsLoading]           = useState(false);
  const [isValidating,         setIsValidating]        = useState(false);
  const [progress,             setProgress]            = useState(0);
  const [results,              setResults]             = useState(null);
  const [fileValidationErrors, setFileValidationErrors]= useState([]);
  const [generalError,         setGeneralError]        = useState(null);
  const [isFileValid,          setIsFileValid]         = useState(false);

  const showToast = (type, message) =>
    toast[type]?.(message, { position: "bottom-right", autoClose: 3500 });

  // ── Manejo de archivo ────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      showToast("error", "Por favor seleccione un archivo Excel válido (.xlsx o .xls)");
      e.target.value = '';
      return;
    }

    setFileForRevalidation(selectedFile);
    setIsValidating(false);
    setFileValidationErrors([]);
    setGeneralError(null);
    setIsFileValid(true); 
    setResults(null);
    setFile(selectedFile);
  };

  // ── Envío ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { showToast("error", "Por favor seleccione un archivo Excel"); return; }

    setIsLoading(true);
    setProgress(0);
    setResults(null);
    setFileValidationErrors([]);
    setGeneralError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("anio", anio);
      formData.append("mes", mes);
      if (proyectoId) {
        formData.append("proyecto_id", proyectoId);
      }

      setProgress(25);
      const res = await pythonApiClient.post(
        "/rrhh/planilla-empleados/carga-masiva/confirm-update",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setProgress(75);

      const r = res.data;
      if (r?.success === false) {
        setFileValidationErrors(r.errores || []);
        setIsFileValid(false);
        showToast("error", "Errores en la carga. Revise los detalles.");
      } else {
        setResults({
          nuevos: r.nuevos || 0,
          message: r.message || "Planillas actualizadas correctamente."
        });
        showToast("success", "Carga masiva completada exitosamente");
        if (onExito) onExito();
      }
      setProgress(100);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errores) {
        setFileValidationErrors(data.errores);
        setIsFileValid(false);
        showToast("error", data.message || "Errores de validación");
      } else {
        const msg = data?.message || data?.detail || "Error interno procesando el archivo, revise la información llenada.";
        setGeneralError(msg);
        showToast("error", msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFile(null); setFileForRevalidation(null);
    setResults(null); setProgress(0);
    setFileValidationErrors([]); setIsFileValid(false);
    setGeneralError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    showToast("info", "Formulario reiniciado");
  };

  // ── Error table helper ────────────────────────────────────────────────────
  const ErrorTable = ({ errores }) => (
    <div className="table-responsive mt-3">
      <table className="table mb-0" style={{ fontSize: '0.9rem' }}>
        <thead className="table-dark">
          <tr>
            <th style={{ width: 110, backgroundColor: '#343a40', color: '#fff' }}>📍 Fila Excel</th>
            <th style={{ backgroundColor: '#343a40', color: '#fff' }}>🚫 Errores de Validación</th>
          </tr>
        </thead>
        <tbody>
          {errores.map((err, idx) => (
            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#fff' }}>
              <td className="fw-bold text-center">
                <span className="badge bg-danger fs-6">{err.fila}</span>
              </td>
              <td>
                <ul className="list-unstyled mb-0">
                  {(Array.isArray(err.errores) ? err.errores : [err.errores]).map((e, i) => (
                    <li key={i} className="mb-1" style={{ color: '#dc3545' }}>
                      <i className="fas fa-times-circle text-danger me-2" />
                      {e}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────────────────────
  if (!show) return null;

  return (
    <>
      <style>{`
        .dh-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 1050;
        }
        .dh-modal-content {
          background: #fff; width: 95%; max-width: 1000px;
          height: 90vh; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          display: flex; flex-direction: column; overflow: hidden;
        }
        .dh-modal-header {
          background: linear-gradient(135deg, #f5af19 0%, #f12711 100%);
          color: #fff; padding: 1.2rem 1.5rem; display: flex;
          justify-content: space-between; align-items: center;
        }
        .dh-modal-title { font-size: 1.25rem; font-weight: 600; margin: 0; display: flex; align-items: center; gap: 0.5rem; }
        .dh-modal-body { padding: 1.5rem; flex: 1; overflow-y: auto; background: #f8f9fa; }
        .dh-modal-footer {
          padding: 1rem 1.5rem; background: #fff; border-top: 1px solid #e9ecef;
          display: flex; justify-content: flex-end; gap: 0.75rem;
        }
        .dh-btn {
          padding: 0.5rem 1rem; border-radius: 6px; font-weight: 500;
          display: inline-flex; align-items: center; gap: 0.5rem; border: none; cursor: pointer; transition: all 0.2s;
        }
        .dh-btn-outline { background: #fff; border: 1px solid #dee2e6; color: #495057; }
        .dh-btn-outline:hover { background: #f8f9fa; border-color: #c1c9d0; }
        .dh-btn-primary { background: #f12711; color: #fff; }
        .dh-btn-primary:hover:not(:disabled) { background: #d0200e; }
        .dh-btn-primary:disabled { background: #f87d71; cursor: not-allowed; }
        .dh-file-area {
          border: 2px dashed #f5af19; border-radius: 8px; padding: 2rem;
          text-align: center; background: #fff; cursor: pointer; transition: all 0.2s;
        }
        .dh-file-area:hover { background: #fdfbf7; border-color: #f12711; }
        .dh-progress-bar {
          height: 8px; border-radius: 4px; background: #e9ecef; overflow: hidden; margin-top: 1rem;
        }
        .dh-progress-fill {
          height: 100%; background: linear-gradient(135deg, #f5af19, #f12711);
          transition: width 0.3s ease;
        }
      `}</style>

      <div className="dh-modal-overlay">
        <div className="dh-modal-content">
          {/* Header */}
          <div className="dh-modal-header">
            <h2 className="dh-modal-title">
              <MdCloudUpload size={24} />
              Actualización Planilla Masiva
            </h2>
          </div>

          {/* Body */}
          <div className="dh-modal-body">
            <div className="row g-4 h-100">
              
              {/* Instrucciones */}
              <div className="col-lg-5 d-flex flex-column gap-3 h-100">
                <div className="card shadow-sm border-0 flex-grow-1" style={{ borderRadius: 10 }}>
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title mb-3" style={{ color: '#f12711' }}>
                      <MdUpload className="me-2" /> Instrucciones
                    </h5>
                    <div className="flex-grow-1 overflow-auto pe-2">
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#374151' }}>
                        <li>Utilice el <strong>Reporte Planilla Mensual</strong> para incluir la planilla de los empleados que pretende actualizar.</li>
                        <li>No modifique los nombres de las columnas en el Excel.</li>
                        <li>Los campos <strong>CALCULADOS</strong> solo son informativos, se volverán a calcular en el sistema.</li>
                      </ul>

                      <div className="alert alert-warning py-2 mt-3 mb-0">
                        <h6 className="alert-heading small fw-bold mb-1"><i className="fas fa-info-circle me-1"></i> AVISO: Campos Calculados</h6>
                        <p className="small mb-0" style={{ fontSize: '0.8rem' }}>
                          No serán leídos por el sistema (ej. Remuneración, Monto Feriado, Sueldo Bruto, Sueldo Neto, Depósitos). 
                          El sistema hará sus propios cálculos internos durante la carga.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Zona de Carga */}
              <div className="col-lg-7 d-flex flex-column h-100">
                <div className="card shadow-sm border-0 flex-grow-1" style={{ borderRadius: 10 }}>
                  <div className="card-body d-flex flex-column p-4">
                    <h5 className="card-title text-secondary mb-4 text-center">Subir Archivo Completado</h5>
                    
                    <label className="dh-file-area mx-auto mb-4" style={{ maxWidth: 400 }}>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        accept=".xlsx, .xls" 
                        className="d-none" 
                      />
                      <div className="mb-3">
                        {file ? (
                          <div className="text-success"><i className="fas fa-file-excel fa-3x mb-2"></i></div>
                        ) : (
                          <div style={{ color: '#f5af19' }}><MdCloudUpload size={48} /></div>
                        )}
                      </div>
                      <h6 className="fw-bold mb-1 text-dark">
                        {file ? file.name : "Seleccionar Archivo"}
                      </h6>
                      <p className="text-muted small mb-0">
                        {file ? (file.size / 1024 / 1024).toFixed(2) + " MB" : "Soporta archivos .xlsx, .xls"}
                      </p>
                    </label>

                    {/* Estados de carga / validación */}
                    {isLoading && (
                      <div className="mt-auto">
                        <div className="d-flex justify-content-between small text-muted mb-1">
                          <span>Procesando...</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="dh-progress-bar">
                          <div className="dh-progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                      </div>
                    )}

                    {results && !fileValidationErrors.length && (
                      <div className="alert alert-success mt-auto py-2 d-flex align-items-center mb-0">
                        <i className="fas fa-check-circle fs-4 me-3"></i>
                        <div>
                          <h6 className="alert-heading mb-1 fw-bold">¡Carga Exitosa!</h6>
                          <p className="mb-0 small">{results.message}</p>
                          <p className="mb-0 small fw-bold mt-1">Nuevos Registros: {results.nuevos}</p>
                        </div>
                      </div>
                    )}

                    {fileValidationErrors.length > 0 && (
                      <div className="mt-3 flex-grow-1 overflow-auto border rounded p-2" style={{ maxHeight: 200, backgroundColor: '#fdfbfb' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                          <h6 className="text-danger mb-0 fw-bold"><i className="fas fa-exclamation-triangle me-1"></i> Errores Encontrados</h6>
                        </div>
                        <ErrorTable errores={fileValidationErrors} />
                      </div>
                    )}

                    {generalError && (
                      <div className="alert alert-danger mt-3 py-2 d-flex align-items-center mb-0">
                        <i className="fas fa-times-circle fs-4 me-3"></i>
                        <div>
                          <h6 className="alert-heading mb-1 fw-bold">Error</h6>
                          <p className="mb-0 small">{generalError}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="dh-modal-footer">
            <button className="dh-btn dh-btn-outline" onClick={resetForm} disabled={isLoading}>
              Reset
            </button>
            <button className="dh-btn dh-btn-outline" onClick={onHide} disabled={isLoading}>
              Cerrar
            </button>
            <button 
              className="dh-btn dh-btn-primary" 
              onClick={handleSubmit}
              disabled={isLoading || !isFileValid}
            >
              {isLoading ? "Procesando..." : "Cargar y Validar"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActualizacionMasivaPlanilla;
